import { MagpieConnection } from 'magpie-lib/connection';
import { defineMessage, defineTopic } from 'magpie-lib/models';
import { VALUES_OF_TABS } from '../../constants/tabs';
import { CollaborationSidebarAudience, CollaborationSidebarSessionId } from 'collaboration-sidebar-common/utils/magpie';
import * as t from 'collaboration-sidebar-common/utils/schema';
import { selectedAssetObjectSchema } from '../../store/collaborationSidebarState';

/**
 * ALL messages MUST include sessionId in their payload so they can target the correct instance of the sidebar
 */
const SessionIdSchema = t.optional(t.literal(CollaborationSidebarSessionId));
const SidebarTabSchema = t.union(...VALUES_OF_TABS.map(tab => t.literal(tab)));
const requestOpenCollaborationSidebarSchema = t.and(t.object({
  sessionId: SessionIdSchema
}), t.partialObject({
  tab: SidebarTabSchema,
  selectedAsset: selectedAssetObjectSchema
}));

/**
 * Sidebar state
 */
export const RequestOpenCollaborationSidebar = defineMessage({
  type: 'requestOpenCollaborationSidebar',
  payloadSchema: requestOpenCollaborationSidebarSchema
});
export const RequestCloseCollaborationSidebar = defineMessage({
  type: 'requestCloseCollaborationSidebar',
  payloadSchema: t.object({
    sessionId: SessionIdSchema
  })
});
export const RequestChangeSidebarTab = defineMessage({
  type: 'requestChangeSidebarTab',
  payloadSchema: t.object({
    sessionId: SessionIdSchema,
    tab: SidebarTabSchema
  })
});
export const RequestPreloadSidebarIframe = defineMessage({
  type: 'requestPreloadSidebarIframe',
  payloadSchema: t.object({
    sessionId: SessionIdSchema
  })
});
export const CollaborationSidebarStateTopic = defineTopic({
  name: 'CollaborationSidebarTopic',
  messages: [RequestOpenCollaborationSidebar, RequestCloseCollaborationSidebar, RequestChangeSidebarTab, RequestPreloadSidebarIframe],
  audience: CollaborationSidebarAudience,
  enableRuntimeValidation: false
});
export const requestOpenCollaborationSidebar = props => {
  const message = RequestOpenCollaborationSidebar.create(Object.assign({
    sessionId: CollaborationSidebarSessionId
  }, props));
  MagpieConnection.get().forTopic(CollaborationSidebarStateTopic).publish(message);
};
export const requestCloseCollaborationSidebar = () => {
  const message = RequestCloseCollaborationSidebar.create({
    sessionId: CollaborationSidebarSessionId
  });
  MagpieConnection.get().forTopic(CollaborationSidebarStateTopic).publish(message);
};
export const requestChangeSidebarTab = props => {
  const message = RequestChangeSidebarTab.create(Object.assign({}, props, {
    sessionId: CollaborationSidebarSessionId
  }));
  MagpieConnection.get().forTopic(CollaborationSidebarStateTopic).publish(message);
};
export const requestPreloadSidebarIframe = () => {
  const message = RequestPreloadSidebarIframe.create({
    sessionId: CollaborationSidebarSessionId
  });
  MagpieConnection.get().forTopic(CollaborationSidebarStateTopic).publish(message);
};
export const requestOpenCollaborationSidebarComments = ({
  selectedAsset
} = {}) => {
  requestOpenCollaborationSidebar({
    tab: 'comments',
    selectedAsset
  });
};

/**
 * Task messages
 */
const RequestChangeStudioCardContextSchema = t.object({
  cardId: t.string,
  label: t.optional(t.string),
  isPlaceholder: t.optional(t.boolean),
  dueDate: t.optional(t.string),
  assigneeId: t.optional(t.string),
  sessionId: SessionIdSchema
});
export const RequestChangeStudioCardContext = defineMessage({
  type: 'studioCardContext',
  payloadSchema: RequestChangeStudioCardContextSchema
});
export const RequestClearStudioCardContext = defineMessage({
  type: 'clearStudioCardContext'
});
export const RequestCreateTask = defineMessage({
  type: 'requestCreateTask'
});
export const TaskOpened = defineMessage({
  type: 'taskOpened',
  payloadSchema: t.object({
    marketingStudioNodeId: t.optional(t.string),
    taskId: t.optional(t.string)
  })
});
export const TasksTopic = defineTopic({
  name: 'TasksTopic',
  messages: [RequestCreateTask, RequestChangeStudioCardContext, RequestClearStudioCardContext, TaskOpened],
  audience: CollaborationSidebarAudience,
  enableRuntimeValidation: false
});
export const requestChangeStudioCardContext = props => {
  const message = RequestChangeStudioCardContext.create(Object.assign({}, props, {
    sessionId: CollaborationSidebarSessionId
  }));
  MagpieConnection.get().forTopic(TasksTopic).publish(message);
};
export const requestClearStudioCardContext = () => {
  const message = RequestClearStudioCardContext.create();
  MagpieConnection.get().forTopic(TasksTopic).publish(message);
};
export const requestCreateTask = () => {
  requestChangeSidebarTab({
    tab: 'tasks'
  });
  const message = RequestCreateTask.create();
  MagpieConnection.get().forTopic(TasksTopic).publish(message);
  requestOpenCollaborationSidebar({
    tab: 'tasks'
  });
};
export const reportTaskOpened = ({
  marketingStudioNodeId,
  taskId
}) => {
  const message = TaskOpened.create({
    marketingStudioNodeId: marketingStudioNodeId !== null && marketingStudioNodeId !== void 0 ? marketingStudioNodeId : undefined,
    taskId: taskId !== null && taskId !== void 0 ? taskId : undefined
  });
  MagpieConnection.get().forTopic(TasksTopic).publish(message);
};
export const requestCreateStudioTask = studioCardContext => {
  var _studioCardContext$du, _studioCardContext$as, _studioCardContext$la, _studioCardContext$is;
  requestChangeStudioCardContext(Object.assign({}, studioCardContext, {
    dueDate: (_studioCardContext$du = studioCardContext.dueDate) !== null && _studioCardContext$du !== void 0 ? _studioCardContext$du : undefined,
    assigneeId: (_studioCardContext$as = studioCardContext.assigneeId) !== null && _studioCardContext$as !== void 0 ? _studioCardContext$as : undefined,
    label: (_studioCardContext$la = studioCardContext.label) !== null && _studioCardContext$la !== void 0 ? _studioCardContext$la : undefined,
    isPlaceholder: (_studioCardContext$is = studioCardContext.isPlaceholder) !== null && _studioCardContext$is !== void 0 ? _studioCardContext$is : undefined
  }));
  requestCreateTask();
};
export const requestViewStudioCardTasks = props => {
  var _props$dueDate, _props$assigneeId, _props$label, _props$isPlaceholder;
  requestOpenCollaborationSidebar({
    tab: 'tasks'
  });
  requestChangeStudioCardContext(Object.assign({}, props, {
    dueDate: (_props$dueDate = props.dueDate) !== null && _props$dueDate !== void 0 ? _props$dueDate : undefined,
    assigneeId: (_props$assigneeId = props.assigneeId) !== null && _props$assigneeId !== void 0 ? _props$assigneeId : undefined,
    label: (_props$label = props.label) !== null && _props$label !== void 0 ? _props$label : undefined,
    isPlaceholder: (_props$isPlaceholder = props.isPlaceholder) !== null && _props$isPlaceholder !== void 0 ? _props$isPlaceholder : undefined
  }));
};

/**
 * Deep Commenting messages
 */
export const RequestScrollToModule = defineMessage({
  type: 'requestScrollToModule',
  payloadSchema: t.object({
    moduleId: t.string,
    location: t.union(t.literal(null), t.string),
    sessionId: t.optional(SessionIdSchema)
  })
});
export const RequestHighlightModule = defineMessage({
  type: 'requestHighlightModule',
  payloadSchema: t.object({
    moduleId: t.union(t.literal(null), t.string),
    fromHover: t.optional(t.boolean),
    sessionId: SessionIdSchema
  })
});
export const CommentNavigationTopic = defineTopic({
  name: 'CommentNavigationTopic',
  messages: [RequestScrollToModule, RequestHighlightModule],
  audience: CollaborationSidebarAudience,
  enableRuntimeValidation: false
});
export const requestScrollToModule = props => {
  const message = RequestScrollToModule.create(Object.assign({}, props, {
    sessionId: CollaborationSidebarSessionId
  }));
  MagpieConnection.get().forTopic(CommentNavigationTopic).publish(message);
};
export const requestHighlightModule = props => {
  const message = RequestHighlightModule.create(Object.assign({
    fromHover: undefined
  }, props, {
    sessionId: CollaborationSidebarSessionId
  }));
  MagpieConnection.get().forTopic(CommentNavigationTopic).publish(message);
};

/**
 * Realtime commenting messages
 */
export const ReportNewComment = defineMessage({
  type: 'reportNewComment',
  payloadSchema: t.object({
    objectId: t.string,
    objectType: t.string,
    parentId: t.optional(t.string),
    sessionId: SessionIdSchema
  })
});
export const RealtimeCommentingTopic = defineTopic({
  name: 'RealtimeCommentingTopic',
  messages: [ReportNewComment],
  audience: CollaborationSidebarAudience,
  enableRuntimeValidation: false
});
export const reportNewComment = ({
  objectId,
  objectType,
  parentId
}) => {
  const message = ReportNewComment.create({
    objectId,
    objectType,
    parentId,
    sessionId: CollaborationSidebarSessionId
  });
  MagpieConnection.get().forTopic(RealtimeCommentingTopic).publish(message);
};